// API Service Layer for Nuroveli Frontend
// Centralized API communication with proper error handling

const API_BASE_URL = process.env.REACT_APP_API_URL;

interface ApiResponse<T = any> {
    success: boolean;
    data?: T;
    message?: string;
    error?: string;
    errors?: Array<{
        type?: string;
        value?: string;
        msg: string;
        path?: string;
        location?: string;
    }>;
}

// Rate limiting and retry mechanism
const requestCache = new Map<string, { timestamp: number; attempts: number; blocked: boolean }>();
const RETRY_DELAYS = [1000, 2000, 5000, 10000]; // Exponential backoff delays
const MAX_RETRY_ATTEMPTS = 4;
const BLOCK_DURATION = 30000; // 30 seconds

// Helper function to get cache key
const getCacheKey = (url: string, options: RequestInit): string => {
    return `${url}_${JSON.stringify(options)}`;
};

// Check if request should be blocked
const shouldBlockRequest = (cacheKey: string): boolean => {
    const cached = requestCache.get(cacheKey);
    if (!cached) return false;

    if (cached.blocked && Date.now() - cached.timestamp < BLOCK_DURATION) {
        return true;
    }

    if (cached.blocked && Date.now() - cached.timestamp >= BLOCK_DURATION) {
        // Reset the cache entry
        requestCache.delete(cacheKey);
        return false;
    }

    return false;
};

// Update request cache
const updateRequestCache = (cacheKey: string, success: boolean): void => {
    const cached = requestCache.get(cacheKey) || { timestamp: Date.now(), attempts: 0, blocked: false };

    if (success) {
        // Reset on success
        requestCache.delete(cacheKey);
    } else {
        cached.attempts += 1;
        cached.timestamp = Date.now();

        if (cached.attempts >= MAX_RETRY_ATTEMPTS) {
            cached.blocked = true;
            console.warn(`Request blocked for ${BLOCK_DURATION / 1000} seconds due to repeated failures:`, cacheKey);
        }

        requestCache.set(cacheKey, cached);
    }
};

// Helper function to get auth headers
const getAuthHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('nuroveli_token');
    return {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
    };
};

// Generic API request function with retry logic
const apiRequest = async <T = any>(
    endpoint: string,
    options: RequestInit = {},
    skipRetry: boolean = false
): Promise<ApiResponse<T>> => {
    const url = `${API_BASE_URL}${endpoint}`;
    const config: RequestInit = {
        headers: getAuthHeaders(),
        ...options,
    };

    const cacheKey = getCacheKey(url, config);

    // Check if request should be blocked
    if (shouldBlockRequest(cacheKey)) {
        return {
            success: false,
            error: 'Too many failed requests. Please wait before trying again.'
        };
    }

    let lastError: Error | null = null;
    const cached = requestCache.get(cacheKey);
    const maxAttempts = skipRetry ? 1 : Math.min(MAX_RETRY_ATTEMPTS, (cached?.attempts || 0) + 1);

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            // Add delay for retry attempts
            if (attempt > 0) {
                const delay = RETRY_DELAYS[Math.min(attempt - 1, RETRY_DELAYS.length - 1)];
                await new Promise(resolve => setTimeout(resolve, delay));
            }

            const response = await fetch(url, config);

            // Check if response is ok
            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch {
                    errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
                }

                // Handle different HTTP status codes
                if (response.status === 401) {
                    // Unauthorized - clear auth tokens
                    localStorage.removeItem('nuroveli_token');
                    localStorage.removeItem('nuroveli_refresh_token');
                    updateRequestCache(cacheKey, false);
                    return {
                        success: false,
                        message: errorData.message || 'Authentication required. Please log in again.',
                        error: errorData.error || 'Unauthorized',
                        errors: errorData.errors,
                        data: { redirect: '/login' } as T
                    };
                }

                if (response.status === 403) {
                    updateRequestCache(cacheKey, false);
                    return {
                        success: false,
                        message: errorData.message || 'Access denied. You do not have permission to perform this action.',
                        error: errorData.error || 'Forbidden',
                        errors: errorData.errors
                    };
                }

                if (response.status === 404) {
                    updateRequestCache(cacheKey, false);
                    return {
                        success: false,
                        message: errorData.message || 'The requested resource was not found.',
                        error: errorData.error || 'Not Found',
                        errors: errorData.errors
                    };
                }

                if (response.status === 400) {
                    updateRequestCache(cacheKey, false);
                    return {
                        success: false,
                        message: errorData.message || 'Invalid request. Please check your input.',
                        error: errorData.error || 'Bad Request',
                        data: errorData.data || errorData,
                        errors: errorData.errors || errorData.data?.errors
                    };
                }

                if (response.status === 409) {
                    updateRequestCache(cacheKey, false);
                    return {
                        success: false,
                        message: errorData.message || 'This request conflicts with existing data.',
                        error: errorData.error || 'Conflict',
                        errors: errorData.errors
                    };
                }

                if (response.status === 422) {
                    updateRequestCache(cacheKey, false);
                    return {
                        success: false,
                        message: errorData.message || 'Unable to process your request. Please check your input.',
                        error: errorData.error || 'Unprocessable Entity',
                        errors: errorData.errors
                    };
                }

                if (response.status === 429) {
                    updateRequestCache(cacheKey, false);
                    return {
                        success: false,
                        message: errorData.message || 'Too many requests. Please wait a moment and try again.',
                        error: errorData.error || 'Rate Limited',
                        errors: errorData.errors
                    };
                }

                if (response.status >= 500) {
                    // Server error - should retry
                    throw new Error(`Server Error: ${response.status} ${response.statusText}`);
                }

                // Other client errors
                updateRequestCache(cacheKey, false);
                return {
                    success: false,
                    message: errorData.message || `Request failed with status ${response.status}`,
                    error: errorData.error || response.statusText,
                    errors: errorData.errors
                };
            }

            const data = await response.json();
            updateRequestCache(cacheKey, true); // Success

            // Backend already returns structured response with success, data, message
            // Return it directly to avoid double nesting
            return data;

        } catch (error) {
            lastError = error instanceof Error ? error : new Error('Unknown error occurred');
            console.error(`API Request attempt ${attempt + 1} failed:`, lastError);

            // Don't retry on network errors if this is the last attempt
            if (attempt === maxAttempts - 1) {
                updateRequestCache(cacheKey, false);
            }
        }
    }

    return {
        success: false,
        error: lastError?.message || 'Network error occurred. Please check your connection and try again.'
    };
};

// Authentication API
export const authApi = {
    login: async (email: string, password: string, rememberMe?: boolean) => {
        return apiRequest('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password, rememberMe })
        });
    },

    register: async (userData: {
        name: string;
        email: string;
        password: string;
    }) => {
        // Use name directly for backend compatibility
        const registrationData = {
            name: userData.name.trim(),
            email: userData.email,
            password: userData.password
        };

        return apiRequest('/auth/register', {
            method: 'POST',
            body: JSON.stringify(registrationData)
        });
    },

    logout: async () => {
        const refreshToken = localStorage.getItem('nuroveli_refresh_token');
        return apiRequest('/auth/logout', {
            method: 'POST',
            body: JSON.stringify({ refreshToken })
        });
    },

    refreshToken: async () => {
        const refreshToken = localStorage.getItem('nuroveli_refresh_token');
        return apiRequest('/auth/refresh', {
            method: 'POST',
            body: JSON.stringify({ refreshToken })
        });
    },

    forgotPassword: async (email: string) => {
        return apiRequest('/auth/forgot-password', {
            method: 'POST',
            body: JSON.stringify({ email })
        });
    },

    resetPassword: async (token: string, password: string) => {
        return apiRequest('/auth/reset-password', {
            method: 'POST',
            body: JSON.stringify({ token, password })
        });
    },

    getCurrentUser: async () => {
        return apiRequest('/auth/me', {
            method: 'GET'
        });
    }
};

// User API
export const userApi = {
    getProfile: async () => {
        return apiRequest('/users/profile');
    },

    getMe: async () => {
        return apiRequest('/users/me');
    },

    updateProfile: async (profileData: {
        name?: string;
        email?: string;
        phone?: string;
        company?: string;
    }) => {
        return apiRequest('/users/profile', {
            method: 'PUT',
            body: JSON.stringify(profileData)
        });
    },

    changePassword: async (currentPassword: string, newPassword: string) => {
        // Get current user ID from localStorage or auth context
        const user = JSON.parse(localStorage.getItem('nuroveli_user') || '{}');
        const userId = user.id;
        
        if (!userId) {
            throw new Error('User ID not found. Please log in again.');
        }
        
        return apiRequest(`/users/${userId}/change-password`, {
            method: 'PUT',
            body: JSON.stringify({ currentPassword, newPassword, confirmPassword: newPassword })
        });
    }
};

// Alerts API
export const alertsApi = {
    getAlerts: async (params?: {
        page?: number;
        limit?: number;
        status?: string;
        priority?: string;
        search?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    queryParams.append(key, String(value));
                }
            });
        }

        const endpoint = `/alerts${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response = await apiRequest(endpoint);
        
        // Transform response data from backend uppercase to frontend lowercase
        if (response.success && response.data && response.data.alerts) {
            response.data.alerts = response.data.alerts.map((alert: any) => ({
                ...alert,
                type: alert.type?.toLowerCase(),
                priority: alert.priority?.toLowerCase(),
                status: alert.status?.toLowerCase(),
                category: alert.category?.toLowerCase(),
                affects_all_users: alert.affectsAllUsers,
                created_at: alert.createdAt,
                updated_at: alert.updatedAt,
                user_id: alert.userId,
                assigned_to: alert.assignedToId,
                alert_data: alert.alertData,
                resolution_notes: alert.resolutionNotes,
                created_by_name: alert.createdBy?.name,
                assigned_to_name: alert.assignedTo?.name
            }));
        }
        
        return response;
    },

    getAlert: async (id: string) => {
        const response = await apiRequest(`/alerts/${id}`);
        
        // Transform response data from backend uppercase to frontend lowercase
        if (response.success && response.data && response.data.alert) {
            response.data.alert = {
                ...response.data.alert,
                type: response.data.alert.type?.toLowerCase(),
                priority: response.data.alert.priority?.toLowerCase(),
                status: response.data.alert.status?.toLowerCase(),
                category: response.data.alert.category?.toLowerCase(),
                affects_all_users: response.data.alert.affectsAllUsers,
                created_at: response.data.alert.createdAt,
                updated_at: response.data.alert.updatedAt,
                user_id: response.data.alert.userId,
                assigned_to: response.data.alert.assignedToId,
                alert_data: response.data.alert.alertData,
                resolution_notes: response.data.alert.resolutionNotes,
                created_by_name: response.data.alert.createdBy?.name,
                assigned_to_name: response.data.alert.assignedTo?.name
            };
        }
        
        return response;
    },

    createAlert: async (alertData: {
        title: string;
        description: string;
        priority: 'low' | 'medium' | 'high' | 'critical';
        status?: 'active' | 'resolved' | 'investigating';
    }) => {
        // Transform frontend lowercase to backend uppercase
        const transformedData = {
            title: alertData.title,
            description: alertData.description,
            priority: alertData.priority.toUpperCase(),
            status: alertData.status?.toUpperCase() || 'ACTIVE'
        };
        
        return apiRequest('/alerts', {
            method: 'POST',
            body: JSON.stringify(transformedData)
        });
    },

    updateAlert: async (id: string, alertData: Partial<{
        title: string;
        description: string;
        priority: 'low' | 'medium' | 'high' | 'critical';
        status: 'active' | 'resolved' | 'investigating';
    }>) => {
        // Transform frontend lowercase to backend uppercase
        const transformedData: any = {};
        if (alertData.title !== undefined) transformedData.title = alertData.title;
        if (alertData.description !== undefined) transformedData.description = alertData.description;
        if (alertData.priority !== undefined) transformedData.priority = alertData.priority.toUpperCase();
        if (alertData.status !== undefined) transformedData.status = alertData.status.toUpperCase();
        
        return apiRequest(`/alerts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(transformedData)
        });
    },

    deleteAlert: async (id: string) => {
        return apiRequest(`/alerts/${id}`, {
            method: 'DELETE'
        });
    },

    acknowledgeAlert: async (id: string, notes?: string) => {
        return apiRequest(`/alerts/${id}/acknowledge`, {
            method: 'PUT',
            body: JSON.stringify({ notes })
        });
    },

    dismissAlert: async (id: string, notes?: string) => {
        return apiRequest(`/alerts/${id}/dismiss`, {
            method: 'PUT',
            body: JSON.stringify({ notes })
        });
    }
};

// Reports API
// Public Reports API for regular users (only published reports)
export const reportsApi = {
    getReports: async (params?: {
        page?: number;
        limit?: number;
        search?: string;
        platform?: string;
        threatType?: string;
        sort_by?: string;
        sort_order?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    // Transform field names for backend
                    let backendKey = key;
                    if (key === 'sort_by') backendKey = 'sortBy';
                    if (key === 'sort_order') backendKey = 'sortOrder';
                    
                    queryParams.append(backendKey, String(value));
                }
            });
        }

        const endpoint = `/reports/public${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        return apiRequest(endpoint);
    },

    getReport: async (id: number) => {
        return apiRequest(`/reports/public/${id}`);
    },

    getOptions: async () => {
        return apiRequest('/reports/meta/options');
    }
};

// Admin Reports API for admin users (all reports)
export const adminReportsApi = {
    getReports: async (params?: {
        page?: number;
        limit?: number;
        search?: string;
        status?: string;
        sort_by?: string;
        sort_order?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    // Transform field names for backend
                    let backendKey = key;
                    if (key === 'sort_by') backendKey = 'sortBy';
                    if (key === 'sort_order') backendKey = 'sortOrder';
                    
                    queryParams.append(backendKey, String(value));
                }
            });
        }

        const endpoint = `/reports${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        return apiRequest(endpoint);
    },

    getReport: async (id: string) => {
        return apiRequest(`/reports/${id}`);
    },

    createReport: async (reportData: {
        title: string;
        description?: string;
        content: string;
        status?: 'draft' | 'published' | 'archived';
        platform?: string;
        threatType?: string;
        postedBy?: string;
        dateDetected?: string;
        affectedCompany?: string;
        dataType?: string;
        proofShared?: boolean;
        threatScore?: number;
        tags?: string[];
        downloadUrl?: string;
    }) => {
        // Transform status enum
        const transformedData: any = {
            ...reportData
        };

        if (reportData.status) {
            transformedData.status = reportData.status.toUpperCase();
        }

        return apiRequest('/reports', {
            method: 'POST',
            body: JSON.stringify(transformedData)
        });
    },

    updateReport: async (id: string, reportData: Partial<{
        title: string;
        description: string;
        content: string;
        status: 'draft' | 'published' | 'archived';
        platform: string;
        threatType: string;
        postedBy: string;
        dateDetected: string;
        affectedCompany: string;
        dataType: string;
        proofShared: boolean;
        threatScore: number;
        tags: string[];
        downloadUrl: string;
    }>) => {
        // Transform status enum
        const transformedData: any = {
            ...reportData
        };

        if (reportData.status) {
            transformedData.status = reportData.status.toUpperCase();
        }

        return apiRequest(`/reports/${id}`, {
            method: 'PUT',
            body: JSON.stringify(transformedData)
        });
    },

    deleteReport: async (id: string) => {
        return apiRequest(`/reports/${id}`, {
            method: 'DELETE'
        });
    },

    getOptions: async () => {
        return apiRequest('/reports/meta/options');
    }
};

// Blogs API
export const blogsApi = {
    getBlogs: async (params?: {
        page?: number;
        limit?: number;
        category?: string;
        search?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    queryParams.append(key, String(value));
                }
            });
        }

        const endpoint = `/blogs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        return apiRequest(endpoint);
    },

    // Get all blogs for admin users (includes draft, published, archived)
    getAdminBlogs: async (params?: {
        page?: number;
        limit?: number;
        category?: string;
        search?: string;
        status?: string;
        author?: string;
        tag?: string;
        sortBy?: string;
        sortOrder?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    queryParams.append(key, String(value));
                }
            });
        }

        const endpoint = `/blogs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        return apiRequest(endpoint);
    },

    // Get only published blogs for public users
    getPublishedBlogs: async (params?: {
        page?: number;
        limit?: number;
        category?: string;
        search?: string;
        author?: string;
        tag?: string;
        sortBy?: string;
        sortOrder?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    queryParams.append(key, String(value));
                }
            });
        }

        const endpoint = `/blogs/published${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        return apiRequest(endpoint);
    },

    getBlog: async (slugOrId: string) => {
        return apiRequest(`/blogs/${slugOrId}`);
    },

    createBlog: async (blogData: {
        title: string;
        content: string;
        excerpt: string;
        category: string;
        featured_image?: string;
        published?: boolean;
        status?: string;
        tags?: string[];
    }) => {
        return apiRequest('/blogs', {
            method: 'POST',
            body: JSON.stringify(blogData)
        });
    },

    updateBlog: async (id: string, blogData: Partial<{
        title: string;
        content: string;
        excerpt: string;
        category: string;
        featured_image: string;
        published: boolean;
        status: string;
        tags: string[];
    }>) => {
        return apiRequest(`/blogs/${id}`, {
            method: 'PUT',
            body: JSON.stringify(blogData)
        });
    },

    deleteBlog: async (id: string) => {
        return apiRequest(`/blogs/${id}`, {
            method: 'DELETE'
        });
    }
};

// Contact API
export const contactApi = {
    submitContact: async (contactData: {
        name: string;
        email: string;
        subject: string;
        message: string;
    }) => {
        return apiRequest('/contact', {
            method: 'POST',
            body: JSON.stringify(contactData)
        });
    }
};

// Leads API
export const leadsApi = {
    submitLead: async (leadData: {
        name: string;
        email: string;
        company?: string;
        phone?: string;
        message?: string;
    }) => {
        return apiRequest('/leads', {
            method: 'POST',
            body: JSON.stringify(leadData)
        });
    }
};

// Custom Monitoring API
export const customMonitoringApi = {
    submitRequest: async (requestData: {
        businessInfo: {
            companyName: string;
            industry: string;
            companySize?: string;
            contactPerson: string;
            email: string;
            phone?: string;
        };
        requirements: {
            monitoringType: string[];
            assets?: string[];
            compliance?: string[];
            budget: string;
            timeline: string;
            priority?: string;
        };
        technicalDetails: {
            currentSecurity: string;
            integrations?: string[];
            specificNeeds?: string;
            challenges?: string;
        };
        additionalInfo: {
            description?: string;
        };
    }) => {
        return apiRequest('/custom-monitoring', {
            method: 'POST',
            body: JSON.stringify(requestData)
        });
    },

    // Get user's own requests (for authenticated users)
    getMyRequests: async (params?: {
        page?: number;
        limit?: number;
        status?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    queryParams.append(key, String(value));
                }
            });
        }

        const endpoint = `/custom-monitoring/my-requests${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        return apiRequest(endpoint);
    },

    // Get all requests (admin only)
    getRequests: async (params?: {
        page?: number;
        limit?: number;
        status?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    queryParams.append(key, String(value));
                }
            });
        }

        const endpoint = `/custom-monitoring${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        return apiRequest(endpoint);
    }
};

// Admin API
export const adminApi = {
    // Users Management
    getUsers: async (params?: {
        page?: number;
        limit?: number;
        search?: string;
        role?: string;
        status?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    // Transform role parameter to roleId for backend
                    if (key === 'role' && value !== 'all') {
                        queryParams.append('roleId', String(value));
                    } else if (key !== 'role') {
                        queryParams.append(key, String(value));
                    }
                }
            });
        }

        const endpoint = `/users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response = await apiRequest(endpoint);
        
        // Transform response data from backend camelCase to frontend snake_case
        if (response.success && response.data && response.data.users) {
            response.data.users = response.data.users.map((user: any) => ({
                ...user,
                email_verified: user?.emailVerified,
                last_login: user.lastLogin,
                created_at: user.createdAt,
                profile_picture: user.profilePicture
            }));
        }
        
        return response;
    },

    getUser: async (id: string) => {
        const response = await apiRequest(`/users/${id}`);
        
        // Transform response data from backend camelCase to frontend snake_case
        if (response.success && response.data && response.data.user) {
            response.data.user = {
                ...response.data.user,
                email_verified: response.data.user.emailVerified,
                last_login: response.data.user.lastLogin,
                created_at: response.data.user.createdAt,
                profile_picture: response.data.user.profilePicture
            };
        }
        
        return response;
    },

    createUser: async (userData: {
        name: string;
        email: string;
        password: string;
        role?: string;
    }) => {
        // Transform frontend field names to backend field names
        const transformedData: any = {
            name: userData.name,
            email: userData.email,
            password: userData.password
        };
        
        if (userData.role) {
            transformedData.roleId = parseInt(userData.role);
        }

        const response = await apiRequest('/users', {
            method: 'POST',
            body: JSON.stringify(transformedData)
        });
        
        // Transform response data from backend camelCase to frontend snake_case
        if (response.success && response.data && response.data.user) {
            response.data.user = {
                ...response.data.user,
                email_verified: response.data?.user?.emailVerified,
                last_login: response.data?.user?.lastLogin,
                created_at: response.data?.user?.createdAt
            };
        }
        
        return response;
    },

    updateUser: async (id: string, userData: {
        name?: string;
        email?: string;
        role?: string;
        email_verified?: boolean;
    }) => {
        // Transform frontend field names to backend field names
        const transformedData: any = {};
        if (userData.name !== undefined) transformedData.name = userData.name;
        if (userData.email !== undefined) transformedData.email = userData.email;
        if (userData.role !== undefined) transformedData.roleId = parseInt(userData.role); // Changed from role to roleId and parse as int
        if (userData.email_verified !== undefined) transformedData.emailVerified = userData.email_verified;

        const response = await apiRequest(`/users/${id}`, {
            method: 'PUT',
            body: JSON.stringify(transformedData)
        });
        
        // Transform response data from backend camelCase to frontend snake_case
        if (response.success && response.data && response.data.user) {
            response.data.user = {
                ...response.data.user,
                email_verified: response.data?.user?.emailVerified,
                last_login: response.data.user.lastLogin,
                created_at: response.data.user.createdAt,
                profile_picture: response.data.user.profilePicture
            };
        }
        
        return response;
    },

    deleteUser: async (id: string) => {
        return apiRequest(`/users/${id}`, {
            method: 'DELETE'
        });
    },

    // Dashboard Statistics
    getDashboardStats: async () => {
        return apiRequest('/users/stats');
    },

    // Leads Management
    getLeads: async (params?: {
        page?: number;
        limit?: number;
        search?: string;
        status?: string;
        sortBy?: string;
        sortOrder?: string;
        dateFrom?: string;
        dateTo?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    queryParams.append(key, String(value));
                }
            });
        }

        const endpoint = `/leads${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        return apiRequest(endpoint);
    },

    getLead: async (id: string) => {
        return apiRequest(`/leads/${id}`);
    },

    createLead: async (leadData: {
        name: string;
        email: string;
        company?: string;
        message?: string;
        source?: string;
    }) => {
        return apiRequest('/leads', {
            method: 'POST',
            body: JSON.stringify(leadData)
        });
    },

    updateLead: async (id: string, leadData: {
        name?: string;
        email?: string;
        company?: string;
        message?: string;
        source?: string;
        status?: string;
    }) => {
        return apiRequest(`/leads/${id}`, {
            method: 'PUT',
            body: JSON.stringify(leadData)
        });
    },

    deleteLead: async (id: string) => {
        return apiRequest(`/leads/${id}`, {
            method: 'DELETE'
        });
    },

    getLeadStats: async () => {
        return apiRequest('/leads/stats');
    },

    // Alerts Management
    getAlerts: async (params?: {
        page?: number;
        limit?: number;
        search?: string;
        status?: string;
        priority?: string;
        category?: string;
        type?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    queryParams.append(key, String(value));
                }
            });
        }

        const endpoint = `/alerts${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response = await apiRequest(endpoint);
        
        // Transform response data from backend uppercase to frontend lowercase
        if (response.success && response.data && response.data.alerts) {
            response.data.alerts = response.data.alerts.map((alert: any) => ({
                ...alert,
                type: alert.type?.toLowerCase(),
                priority: alert.priority?.toLowerCase(),
                status: alert.status?.toLowerCase(),
                category: alert.category?.toLowerCase(),
                affects_all_users: alert.affectsAllUsers,
                created_at: alert.createdAt,
                updated_at: alert.updatedAt,
                user_id: alert.userId,
                assigned_to: alert.assignedToId,
                alert_data: alert.alertData,
                resolution_notes: alert.resolutionNotes,
                created_by_name: alert.createdBy?.name,
                assigned_to_name: alert.assignedTo?.name
            }));
        }
        
        return response;
    },

    createAlert: async (alertData: {
        title: string;
        description?: string;
        message?: string;
        type?: string;
        priority: string;
        status?: string;
        category: string;
        source?: string;
        affects_all_users?: boolean;
    }) => {
        // Transform frontend lowercase to backend uppercase
        const transformedData: any = {
            title: alertData.title,
            description: alertData.description,
            message: alertData.message,
            type: alertData.type?.toUpperCase() || 'INFO',
            priority: alertData.priority.toUpperCase(),
            status: alertData.status?.toUpperCase() || 'ACTIVE',
            category: alertData.category.toUpperCase(),
            source: alertData.source,
            affectsAllUsers: alertData.affects_all_users || false
        };

        return apiRequest('/alerts', {
            method: 'POST',
            body: JSON.stringify(transformedData)
        });
    },

    updateAlert: async (id: string, alertData: {
        title?: string;
        description?: string;
        message?: string;
        type?: string;
        priority?: string;
        status?: string;
        category?: string;
        source?: string;
        affects_all_users?: boolean;
    }) => {
        // Transform frontend lowercase to backend uppercase
        const transformedData: any = {};
        if (alertData.title !== undefined) transformedData.title = alertData.title;
        if (alertData.description !== undefined) transformedData.description = alertData.description;
        if (alertData.message !== undefined) transformedData.message = alertData.message;
        if (alertData.type !== undefined) transformedData.type = alertData.type.toUpperCase();
        if (alertData.priority !== undefined) transformedData.priority = alertData.priority.toUpperCase();
        if (alertData.status !== undefined) transformedData.status = alertData.status.toUpperCase();
        if (alertData.category !== undefined) transformedData.category = alertData.category.toUpperCase();
        if (alertData.source !== undefined) transformedData.source = alertData.source;
        if (alertData.affects_all_users !== undefined) transformedData.affectsAllUsers = alertData.affects_all_users;

        return apiRequest(`/alerts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(transformedData)
        });
    },

    deleteAlert: async (id: string) => {
        return apiRequest(`/alerts/${id}`, {
            method: 'DELETE'
        });
    },

    // Bulk operations
    bulkUpdateAlerts: async (alertIds: number[], action: string, data?: {
        status?: string;
        assignedToId?: number;
        resolutionNotes?: string;
    }) => {
        const transformedData: any = {
            action,
            alertIds,
            data: {}
        };

        if (data) {
            if (data.status) transformedData.data.status = data.status.toUpperCase();
            if (data.assignedToId) transformedData.data.assignedToId = data.assignedToId;
            if (data.resolutionNotes) transformedData.data.resolutionNotes = data.resolutionNotes;
        }

        return apiRequest('/alerts/bulk', {
            method: 'POST',
            body: JSON.stringify(transformedData)
        });
    },

    getAlertOptions: async () => {
        return apiRequest('/alerts/meta/options');
    },

    // Reports Management
    getReports: async (params?: {
        page?: number;
        limit?: number;
        search?: string;
        status?: string;
        severity?: string;
        platform?: string;
        threat_type?: string;
        sort_by?: string;
        sort_order?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    queryParams.append(key, String(value));
                }
            });
        }

        const endpoint = `/reports${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response = await apiRequest(endpoint);
        
        // Transform response data from backend camelCase to frontend snake_case
        if (response.success && response.data && response.data.reports) {
            response.data.reports = response.data.reports.map((report: any) => ({
                ...report,
                status: report.status?.toLowerCase(),
                threat_type: report.threatType,
                posted_by: report.postedBy,
                date_detected: report.dateDetected,
                affected_company: report.affectedCompany,
                data_type: report.dataType,
                proof_shared: report.proofShared,
                threat_score: report.threatScore,
                download_url: report.downloadUrl,
                created_at: report.createdAt,
                updated_at: report.updatedAt,
                created_by_name: report.createdBy?.name
            }));
        }
        
        return response;
    },

    getReport: async (id: string) => {
        const response = await apiRequest(`/reports/${id}`);
        
        // Transform response data from backend camelCase to frontend snake_case
        if (response.success && response.data && response.data.report) {
            response.data.report = {
                ...response.data.report,
                status: response.data.report.status?.toLowerCase(),
                threat_type: response.data.report.threatType,
                posted_by: response.data.report.postedBy,
                date_detected: response.data.report.dateDetected,
                affected_company: response.data.report.affectedCompany,
                data_type: response.data.report.dataType,
                proof_shared: response.data.report.proofShared,
                threat_score: response.data.report.threatScore,
                download_url: response.data.report.downloadUrl,
                created_at: response.data.report.createdAt,
                updated_at: response.data.report.updatedAt,
                created_by_name: response.data.report.createdBy?.name
            };
        }
        
        return response;
    },

    createReport: async (reportData: {
        title: string;
        description?: string;
        content: string;
        status?: 'draft' | 'published' | 'archived';
        platform?: string;
        threat_type?: string;
        posted_by?: string;
        date_detected?: string;
        affected_company?: string;
        data_type?: string;
        proof_shared?: boolean;
        threat_score?: number;
        tags?: string[] | string;
        download_url?: string;
    }) => {
        // Transform frontend snake_case to backend camelCase
        const transformedData: any = {
            title: reportData.title,
            description: reportData.description,
            content: reportData.content,
            status: reportData.status?.toUpperCase() || 'DRAFT',
            platform: reportData.platform,
            threatType: reportData.threat_type,
            postedBy: reportData.posted_by,
            dateDetected: reportData.date_detected,
            affectedCompany: reportData.affected_company,
            dataType: reportData.data_type,
            proofShared: reportData.proof_shared || false,
            threatScore: reportData.threat_score,
            tags: reportData.tags,
            downloadUrl: reportData.download_url
        };

        return apiRequest('/reports', {
            method: 'POST',
            body: JSON.stringify(transformedData)
        });
    },

    updateReport: async (id: string, reportData: {
        title?: string;
        description?: string;
        content?: string;
        status?: 'draft' | 'published' | 'archived';
        platform?: string;
        threat_type?: string;
        posted_by?: string;
        date_detected?: string;
        affected_company?: string;
        data_type?: string;
        proof_shared?: boolean;
        threat_score?: number;
        tags?: string[] | string;
        download_url?: string;
    }) => {
        // Transform frontend fields to backend format
        const transformedData: any = {
            ...reportData
        };

        // Transform field names
        if (reportData.threat_type !== undefined) {
            transformedData.threatType = reportData.threat_type;
            delete transformedData.threat_type;
        }
        if (reportData.posted_by !== undefined) {
            transformedData.postedBy = reportData.posted_by;
            delete transformedData.posted_by;
        }
        if (reportData.date_detected !== undefined) {
            transformedData.dateDetected = reportData.date_detected;
            delete transformedData.date_detected;
        }
        if (reportData.affected_company !== undefined) {
            transformedData.affectedCompany = reportData.affected_company;
            delete transformedData.affected_company;
        }
        if (reportData.data_type !== undefined) {
            transformedData.dataType = reportData.data_type;
            delete transformedData.data_type;
        }
        if (reportData.proof_shared !== undefined) {
            transformedData.proofShared = reportData.proof_shared;
            delete transformedData.proof_shared;
        }
        if (reportData.threat_score !== undefined) {
            transformedData.threatScore = reportData.threat_score;
            delete transformedData.threat_score;
        }
        if (reportData.download_url !== undefined) {
            transformedData.downloadUrl = reportData.download_url;
            delete transformedData.download_url;
        }

        // Transform status enum
        if (reportData.status) {
            transformedData.status = reportData.status.toUpperCase();
        }

        return apiRequest(`/reports/${id}`, {
            method: 'PUT',
            body: JSON.stringify(transformedData)
        });
    },

    deleteReport: async (id: string) => {
        return apiRequest(`/reports/${id}`, {
            method: 'DELETE'
        });
    },

    getReportStats: async () => {
        return apiRequest('/reports/stats');
    },

    getReportOptions: async () => {
        return apiRequest('/reports/meta/options');
    },

    // Custom Monitoring Management
    getCustomMonitoringRequests: async (params?: {
        page?: number;
        limit?: number;
        search?: string;
        status?: string;
        sortBy?: string;
        sortOrder?: string;
        dateFrom?: string;
        dateTo?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    queryParams.append(key, String(value));
                }
            });
        }

        const endpoint = `/custom-monitoring${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        return apiRequest(endpoint);
    },

    getCustomMonitoringRequest: async (id: string) => {
        return apiRequest(`/custom-monitoring/${id}`);
    },

    updateCustomMonitoringRequest: async (id: string, requestData: {
        status?: 'NEW' | 'REVIEWING' | 'QUOTED' | 'APPROVED' | 'COMPLETED' | 'REJECTED';
        adminNotes?: string;
    }) => {
        return apiRequest(`/custom-monitoring/${id}`, {
            method: 'PUT',
            body: JSON.stringify(requestData)
        });
    },

    deleteCustomMonitoringRequest: async (id: string) => {
        return apiRequest(`/custom-monitoring/${id}`, {
            method: 'DELETE'
        });
    },

    getCustomMonitoringStats: async () => {
        return apiRequest('/custom-monitoring/stats');
    },

    // Role Management
    getRoles: async (params?: {
        page?: number;
        limit?: number;
        search?: string;
        isActive?: boolean;
    }) => {
        const queryParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    queryParams.append(key, String(value));
                }
            });
        }

        const endpoint = `/admin/roles${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        return apiRequest(endpoint);
    },

    createRole: async (roleData: {
        name: string;
        description?: string;
        permissions: {
            users?: {
                create?: boolean;
                read?: boolean;
                update?: boolean;
                delete?: boolean;
                verify?: boolean;
            };
            alerts?: {
                create?: boolean;
                read?: boolean;
                update?: boolean;
                delete?: boolean;
            };
            reports?: {
                create?: boolean;
                read?: boolean;
                update?: boolean;
                delete?: boolean;
            };
            blogs?: {
                create?: boolean;
                read?: boolean;
                update?: boolean;
                delete?: boolean;
            };
            customMonitoring?: {
                create?: boolean;
                read?: boolean;
                update?: boolean;
                delete?: boolean;
            };
            leads?: {
                create?: boolean;
                read?: boolean;
                update?: boolean;
                delete?: boolean;
            };
            roleManagement?: {
                create?: boolean;
                read?: boolean;
                update?: boolean;
                delete?: boolean;
                assign?: boolean;
            };
        };
    }) => {
        return apiRequest('/admin/roles', {
            method: 'POST',
            body: JSON.stringify(roleData)
        });
    },

    updateRole: async (id: string, roleData: {
        name?: string;
        description?: string;
        permissions?: {
            users?: {
                create?: boolean;
                read?: boolean;
                update?: boolean;
                delete?: boolean;
                verify?: boolean;
            };
            alerts?: {
                create?: boolean;
                read?: boolean;
                update?: boolean;
                delete?: boolean;
            };
            reports?: {
                create?: boolean;
                read?: boolean;
                update?: boolean;
                delete?: boolean;
            };
            blogs?: {
                create?: boolean;
                read?: boolean;
                update?: boolean;
                delete?: boolean;
            };
            customMonitoring?: {
                create?: boolean;
                read?: boolean;
                update?: boolean;
                delete?: boolean;
            };
            leads?: {
                create?: boolean;
                read?: boolean;
                update?: boolean;
                delete?: boolean;
            };
            roleManagement?: {
                create?: boolean;
                read?: boolean;
                update?: boolean;
                delete?: boolean;
                assign?: boolean;
            };
        };
        isActive?: boolean;
    }) => {
        return apiRequest(`/admin/roles/${id}`, {
            method: 'PUT',
            body: JSON.stringify(roleData)
        });
    },

    deleteRole: async (id: string) => {
        return apiRequest(`/admin/roles/${id}`, {
            method: 'DELETE'
        });
    },

    getRoleAssignments: async (params?: {
        page?: number;
        limit?: number;
        search?: string;
        roleId?: number;
        userId?: number;
        isActive?: boolean;
    }) => {
        const queryParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    queryParams.append(key, String(value));
                }
            });
        }

        const endpoint = `/admin/roles/assignments${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        return apiRequest(endpoint);
    },

    assignRole: async (assignmentData: {
        userId: number;
        roleId: number;
        expiresAt?: string;
    }) => {
        return apiRequest('/admin/roles/assign', {
            method: 'POST',
            body: JSON.stringify(assignmentData)
        });
    },

    removeRoleAssignment: async (assignmentId: string) => {
        return apiRequest(`/admin/roles/assignments/${assignmentId}`, {
            method: 'DELETE'
        });
    },

    getAvailablePermissions: async () => {
        return apiRequest('/admin/roles/permissions');
    },

    getPermissionStructure: async () => {
        return apiRequest('/admin/roles/meta/permissions');
    },

    getUserRoles: async (userId: string) => {
        return apiRequest(`/admin/roles/user/${userId}/roles`);
    }
};

// Health Check API
export const healthApi = {
    check: async () => {
        return apiRequest('/health');
    }
};

// Export default API object
const api = {
    auth: authApi,
    user: userApi,
    alerts: alertsApi,
    reports: reportsApi,
    adminReports: adminReportsApi,
    blogs: blogsApi,
    contact: contactApi,
    leads: leadsApi,
    customMonitoring: customMonitoringApi,
    admin: adminApi,
    health: healthApi
};

export default api;
